import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Note } from '@/types';

export const NOTE_MAX_LENGTH = 80;
const NOTE_TTL_MS = 24 * 60 * 60 * 1000;

export interface NoteFilterContext {
  viewerUid: string;
  blockedUids: string[];
  blockedByUids: string[];
}

/** A note whose author fields the stampNoteAuthor trigger has filled in. */
export type StampedNote = Note & { authorName: string };

export function isStampedNote(note: Note): note is StampedNote {
  return typeof note.authorName === 'string' && note.authorName.length > 0;
}

/** The viewer's note bypasses the visibility filter. */
export function filterAndSortNotes(
  notes: Note[],
  ctx: NoteFilterContext
): { myNote: Note | null; entries: Note[] } {
  let myNote: Note | null = null;
  const entries: Note[] = [];

  for (const note of notes) {
    if (note.uid === ctx.viewerUid) {
      myNote = note;
      continue;
    }
    if (note.visible === false) continue;
    if (ctx.blockedUids.includes(note.uid) || ctx.blockedByUids.includes(note.uid)) continue;
    entries.push(note);
  }

  entries.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  return { myNote, entries };
}

export const noteService = {
  subscribeToActiveNotes(
    callback: (notes: Note[]) => void,
    onError?: (error: Error) => void
  ): () => void {
    const notesQuery = query(collection(db, 'notes'), where('expiresAt', '>', Timestamp.now()));
    return onSnapshot(
      notesQuery,
      snap => {
        callback(snap.docs.map(docSnap => docSnap.data() as Note));
      },
      error => {
        console.error('Notes subscription failed:', error);
        if (onError) {
          onError(error);
        } else {
          callback([]);
        }
      }
    );
  },

  /** Author display fields are stamped server-side by stampNoteAuthor. */
  async upsertNote(uid: string, text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) throw new Error('Note text is required');
    if (trimmed.length > NOTE_MAX_LENGTH) {
      throw new Error(`Note exceeds ${NOTE_MAX_LENGTH} characters`);
    }

    const now = new Date();
    const note = {
      uid,
      text: trimmed,
      createdAt: now.toISOString(),
      expiresAt: Timestamp.fromMillis(now.getTime() + NOTE_TTL_MS),
    };
    await setDoc(doc(db, 'notes', uid), note);
  },

  async deleteNote(uid: string): Promise<void> {
    await deleteDoc(doc(db, 'notes', uid));
  },
};
